/**
 * DatasetPlanReview - review + execute a multi-file dataset upload plan.
 *
 * Shown after /ingestion/connect/datasets/inspect returns a plan. Minimal by
 * default, fully configurable on demand:
 *   • a one-line header with file count;
 *   • a single collapsed "notes" row holding dataset warnings, key-collision
 *     namespacing and cross-file duplicate resolutions;
 *   • ONE card per file (TableCard) carrying the import switch, summary
 *     chips, and — under Configure — the per-column adjustments list and
 *     the full column editor;
 *   • suggested goals (created on demand once the import completes).
 *
 * On "Import" it POSTs the (possibly user-edited) plan to /datasets/import
 * and switches to a live batch progress view polling /datasets/<id> until
 * every file reaches a terminal state.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Button, Stack, Chip, Alert, Divider, Collapse,
  LinearProgress, CircularProgress, Switch, Tooltip, useTheme,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BlockIcon from '@mui/icons-material/Block';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FunctionsIcon from '@mui/icons-material/Functions';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import FlagIcon from '@mui/icons-material/Flag';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import TuneIcon from '@mui/icons-material/Tune';

import TableCard from './TableCard';
import { compactNum } from './connectorMeta';
import {
  fetchRequest as datasetImportRequest,
  clear as clearDatasetImport,
} from '../../redux/actions/datasources/datasetImport';
import {
  fetchRequest as createGoalRequest,
  clear as clearCreateGoal,
} from '../../redux/actions/datasources/createGoal';

/**
 * DatasetOverview - the visual header of the review screen: headline stats
 * (rows / series / events) plus a per-file row-volume bar chart. Bar widths
 * use a sqrt scale so a huge intraday file doesn't flatten the others, and
 * files switched off dim live.
 */
function DatasetOverview({ plan, theme }) {
  const files = plan?.files || [];
  const active = files.filter((f) => !f.skip_file);

  const seriesCount = active.reduce((n, f) => n + (f.table?.columns || [])
    .filter((c) => c.included && !c.is_timestamp && !c.is_entity && !c.is_event).length, 0);
  const eventCount = active.reduce((n, f) => n + (f.table?.columns || [])
    .filter((c) => c.is_event).length, 0);
  const totalRows = active.reduce((n, f) => n + (f.table?.row_estimate || 0), 0);
  const maxSqrt = Math.max(...files.map((f) => Math.sqrt(f.table?.row_estimate || 0)), 1);

  const stats = [
    { label: 'files', value: `${active.length}${active.length !== files.length ? ` / ${files.length}` : ''}` },
    { label: 'rows', value: `~${compactNum(totalRows)}` },
    { label: 'series', value: seriesCount },
    ...(eventCount > 0 ? [{ label: 'events', value: eventCount }] : []),
  ];

  return (
    <Paper elevation={0} sx={{
      border: `1px solid ${theme.palette.divider}`, borderRadius: 2,
      bgcolor: theme.palette.background.paper, p: 2, mb: 1.5,
    }}>
      <Stack direction="row" spacing={3} sx={{ mb: 1.75 }}>
        {stats.map((s) => (
          <Box key={s.label}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.15 }}>{s.value}</Typography>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
              textTransform: 'uppercase', color: 'text.secondary',
            }}>
              {s.label}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Stack spacing={0.6}>
        {files.map((f) => {
          const rows = f.table?.row_estimate || 0;
          const pct = Math.max((Math.sqrt(rows) / maxSqrt) * 100, 2);
          const off = !!f.skip_file;
          return (
            <Stack key={f.filename} direction="row" alignItems="center" spacing={1}
              sx={{ opacity: off ? 0.4 : 1, transition: 'opacity 0.2s' }}>
              <Typography noWrap title={f.filename} sx={{
                fontSize: 11.5, fontFamily: 'monospace', color: 'text.secondary',
                width: 168, flexShrink: 0, textAlign: 'right',
              }}>
                {f.filename}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Tooltip title={`~${rows.toLocaleString()} rows${off ? ' · not imported' : ''}`}>
                  <Box sx={{
                    width: `${pct}%`, height: 10, borderRadius: 1,
                    transition: 'width 0.3s, background 0.2s',
                    background: off
                      ? theme.palette.action.disabledBackground
                      : `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  }} />
                </Tooltip>
              </Box>
              <Typography sx={{
                fontSize: 11, color: 'text.secondary', width: 52, flexShrink: 0,
                textAlign: 'right', fontVariantNumeric: 'tabular-nums',
              }}>
                {compactNum(rows)}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}

const DECISION_META = {
  skip_file: { icon: <VisibilityOffIcon sx={{ fontSize: 14 }} />, label: 'Reference data', color: 'default' },
  namespace: { icon: <CallSplitIcon sx={{ fontSize: 14 }} />, label: 'Keys prefixed', color: 'info' },
  exclude: { icon: <BlockIcon sx={{ fontSize: 14 }} />, label: 'Excluded', color: 'error' },
  duplicate_drop: { icon: <ContentCopyIcon sx={{ fontSize: 14 }} />, label: 'Duplicate dropped', color: 'warning' },
  value_map: { icon: <FunctionsIcon sx={{ fontSize: 14 }} />, label: 'Numeric encoding', color: 'success' },
  timestamp_shift: { icon: <ScheduleIcon sx={{ fontSize: 14 }} />, label: 'Event-time shift', color: 'info' },
  derive_row_expr: { icon: <FunctionsIcon sx={{ fontSize: 14 }} />, label: 'Derived column', color: 'success' },
  derive_resample: { icon: <FunctionsIcon sx={{ fontSize: 14 }} />, label: 'Derived series', color: 'success' },
};

function DecisionRow({ decision }) {
  const meta = DECISION_META[decision.kind]
    || { icon: <TuneIcon sx={{ fontSize: 14 }} />, label: decision.kind, color: 'default' };
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.4 }}>
      <Chip size="small" variant="outlined" color={meta.color} icon={meta.icon}
        label={decision.column ? `${meta.label} · ${decision.column}` : meta.label}
        sx={{ height: 22, fontSize: 11, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', pt: 0.25 }}>
        {decision.detail}
      </Typography>
    </Stack>
  );
}

const TERMINAL = ['completed', 'failed', 'cancelled', 'skipped'];

export default function DatasetPlanReview({ datasetId, plan: initialPlan, onBack, onDone }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [plan, setPlan] = useState(initialPlan);
  const [expanded, setExpanded] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [error, setError] = useState(null);

  // Import start + batch polling live in the datasetImport saga; the slice's
  // response is the live GET /datasets/<id> payload.
  const importSlice = useSelector((s) => s.datasource_dataset_import) || { response: null, isLoading: false, error: null };
  const batch = importSlice.response || null;
  const importing = importSlice.isLoading;
  // target_key -> 'creating'|'done'|'failed' (accumulated by the saga).
  const goalsCreated = useSelector((s) => s.datasource_create_goal?.response) || {};

  const card = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    bgcolor: theme.palette.background.paper,
  };
  const sectionLabel = {
    fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
    color: 'text.secondary', mb: 1,
  };

  // Stop the batch poller + reset the slices when the review unmounts
  // (mirrors the old clearTimeout(pollTimer) cleanup and the per-mount
  // goalsCreated local state).
  useEffect(() => () => {
    dispatch(clearDatasetImport());
    dispatch(clearCreateGoal());
  }, [dispatch]);

  // ---- plan edits -------------------------------------------------------
  const setFile = useCallback((filename, patch) => {
    setPlan((p) => ({
      ...p,
      files: p.files.map((f) => (f.filename === filename ? { ...f, ...patch } : f)),
    }));
  }, []);

  const updateColumn = useCallback((filename, idx, patch) => {
    setPlan((p) => ({
      ...p,
      files: p.files.map((f) => {
        if (f.filename !== filename) return f;
        const columns = f.table.columns.map((c, i) => {
          if (i !== idx) {
            if (patch.is_timestamp) return { ...c, is_timestamp: false };
            if (patch.is_entity) return { ...c, is_entity: false };
            return c;
          }
          return { ...c, ...patch };
        });
        const ts = columns.find((c) => c.is_timestamp);
        const ent = columns.find((c) => c.is_entity);
        return {
          ...f,
          table: {
            ...f.table,
            columns,
            timestamp_column: ts?.source_field || null,
            entity_column: ent?.source_field || null,
          },
        };
      }),
    }));
  }, []);

  // ---- import + polling --------------------------------------------------
  const runImport = useCallback(() => {
    setError(null);
    dispatch(datasetImportRequest({ datasetId, plan }));
  }, [datasetId, plan, dispatch]);

  useEffect(() => {
    if (importSlice.error) {
      setError(importSlice.error);
      dispatch(clearDatasetImport());
    }
  }, [importSlice.error, dispatch]);

  const createGoal = useCallback((goalSuggestion) => {
    dispatch(createGoalRequest({
      target_key: goalSuggestion.target_key,
      goal_type: goalSuggestion.goal_type || 'forecast_value',
    }));
  }, [dispatch]);

  // ---- derived view state --------------------------------------------------
  const importableCount = useMemo(
    () => (plan?.files || []).filter((f) => !f.skip_file).length,
    [plan],
  );
  // Every imported file needs a time column (same rule as single-file import).
  const missingTimestamp = useMemo(
    () => (plan?.files || [])
      .filter((f) => !f.skip_file && !f.table?.timestamp_column)
      .map((f) => f.filename),
    [plan],
  );
  const collisionEntries = Object.entries(plan?.collisions || {});
  const noteCount = (plan?.warnings || []).length
    + (collisionEntries.length > 0 ? 1 : 0)
    + (plan?.duplicates || []).length;
  const finished = batch && (batch.status === 'completed' || batch.status === 'completed_with_errors' || batch.status === 'failed');

  // =========================================================== PROGRESS VIEW
  if (batch) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
          {finished
            ? (batch.status === 'failed'
              ? <ErrorOutlineIcon color="error" />
              : <CheckCircleIcon color="success" />)
            : <CircularProgress size={20} />}
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {finished
              ? (batch.status === 'completed' ? 'Dataset imported'
                : batch.status === 'completed_with_errors' ? 'Imported with some errors'
                  : 'Import failed')
              : 'Importing dataset…'}
          </Typography>
        </Stack>

        <Stack spacing={1}>
          {(batch.files || []).map((f) => (
            <Paper key={f.filename} elevation={0} sx={{ ...card, p: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, flex: 1 }} noWrap>{f.filename}</Typography>
                {f.rows_inserted != null && f.status === 'completed' && (
                  <Typography variant="caption" color="text.secondary">
                    {Number(f.rows_inserted).toLocaleString()} rows
                  </Typography>
                )}
                <Chip size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }}
                  color={f.status === 'completed' ? 'success' : (f.status === 'failed' || f.status === 'cancelled') ? 'error' : 'default'}
                  label={f.status} />
              </Stack>
              {!TERMINAL.includes(f.status) && (
                <LinearProgress variant={f.progress != null ? 'determinate' : 'indeterminate'}
                  value={f.progress || 0} sx={{ mt: 1, height: 6, borderRadius: 1 }} />
              )}
              {f.error && <Alert severity="error" sx={{ mt: 1, py: 0.25, fontSize: 12.5 }}>{f.error}</Alert>}
            </Paper>
          ))}
          {(batch.skipped || []).map((s) => (
            <Paper key={s.filename} elevation={0} sx={{ ...card, p: 1.5, opacity: 0.65 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <VisibilityOffIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, flex: 1 }} noWrap>{s.filename}</Typography>
                <Tooltip title={s.reason}><Chip size="small" variant="outlined" label="not imported" sx={{ height: 20, fontSize: 11 }} /></Tooltip>
              </Stack>
            </Paper>
          ))}
        </Stack>

        {finished && (plan?.suggested_goals || []).length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography sx={sectionLabel}><FlagIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: -2 }} />Suggested goals</Typography>
            <Stack spacing={1}>
              {plan.suggested_goals.map((gs) => (
                <Paper key={gs.target_key} elevation={0} sx={{ ...card, p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>
                        {gs.goal_type} · <code>{gs.target_key}</code>
                      </Typography>
                      {gs.reason && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{gs.reason}</Typography>
                      )}
                    </Box>
                    {goalsCreated[gs.target_key] === 'done' ? (
                      <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Goal created" sx={{ height: 24, fontSize: 11 }} />
                    ) : (
                      <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}
                        disabled={goalsCreated[gs.target_key] === 'creating'}
                        color={goalsCreated[gs.target_key] === 'failed' ? 'error' : 'primary'}
                        startIcon={goalsCreated[gs.target_key] === 'creating' ? <CircularProgress size={14} /> : <FlagIcon sx={{ fontSize: 15 }} />}
                        onClick={() => createGoal(gs)}>
                        {goalsCreated[gs.target_key] === 'failed' ? 'Retry create goal' : 'Create goal'}
                      </Button>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {finished && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="contained" sx={{ textTransform: 'none' }} onClick={onDone}>Done</Button>
          </Stack>
        )}
      </Box>
    );
  }

  // =========================================================== REVIEW VIEW
  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Review import</Typography>
        <Typography variant="body2" color="text.secondary">
          {importableCount} of {plan.files.length} file{plan.files.length === 1 ? '' : 's'} selected
        </Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <DatasetOverview plan={plan} theme={theme} />

      {noteCount > 0 && (
        <Paper elevation={0} sx={{ ...card, mb: 1.5, overflow: 'hidden' }}>
          <Stack direction="row" alignItems="center" spacing={1}
            onClick={() => setNotesOpen((v) => !v)}
            sx={{ p: 1.25, cursor: 'pointer', userSelect: 'none' }}>
            <InfoOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: 13, color: 'text.secondary', flex: 1 }}>
              {noteCount} note{noteCount === 1 ? '' : 's'} about this dataset
            </Typography>
            <ExpandMoreIcon sx={{
              fontSize: 19, color: 'text.secondary',
              transform: notesOpen ? 'rotate(180deg)' : 'none', transition: '0.2s',
            }} />
          </Stack>
          <Collapse in={notesOpen}>
            <Divider />
            <Box sx={{ px: 1.5, py: 1 }}>
              {collisionEntries.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.4 }}>
                  <Chip size="small" variant="outlined" color="info" icon={<CallSplitIcon sx={{ fontSize: 14 }} />}
                    label="shared keys" sx={{ height: 22, fontSize: 11, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', pt: 0.25 }}>
                    {collisionEntries.slice(0, 6).map(([k]) => k).join(', ')}
                    {collisionEntries.length > 6 ? '…' : ''} appear in multiple files — keys are prefixed
                    per file so the series never merge.
                  </Typography>
                </Stack>
              )}
              {(plan.duplicates || []).map((d, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.4 }}>
                  <Chip size="small" variant="outlined" color="warning" icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                    label={d.quantity || 'duplicate'} sx={{ height: 22, fontSize: 11, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', pt: 0.25 }}>
                    Keeping <strong>{d.keep.file}:{d.keep.column}</strong>, dropping{' '}
                    {d.drop.map((p) => `${p.file}:${p.column}`).join(', ')}. {d.reason}
                  </Typography>
                </Stack>
              ))}
              {(plan.warnings || []).map((w, i) => (
                <Typography key={i} sx={{ fontSize: 12.5, color: 'text.secondary', py: 0.4 }}>
                  • {w}
                </Typography>
              ))}
            </Box>
          </Collapse>
        </Paper>
      )}

      <Stack spacing={1.25} sx={{ mb: 2 }}>
        {plan.files.map((f) => {
          const adjustments = (f.decisions || []).filter((d) => d.kind !== 'skip_file');
          return (
            <TableCard
              key={f.filename}
              table={f.table}
              selectable={false}
              dimmed={f.skip_file}
              expanded={expanded === f.filename}
              onToggleExpand={f.skip_file ? undefined : () => setExpanded(expanded === f.filename ? null : f.filename)}
              updateColumn={(idx, patch) => updateColumn(f.filename, idx, patch)}
              showScope={false}
              extraChips={(
                <>
                  {f.key_prefix && !f.skip_file && (
                    <Tooltip title="Series keys are prefixed per file because another file produces the same column names">
                      <Chip size="small" variant="outlined" color="info" icon={<CallSplitIcon sx={{ fontSize: 13 }} />}
                        label={`${f.key_prefix}.*`} sx={{ height: 20, fontSize: 11 }} />
                    </Tooltip>
                  )}
                  {adjustments.length > 0 && !f.skip_file && (
                    <Tooltip title="Open Configure to see and change each adjustment">
                      <Chip size="small" variant="outlined" icon={<TuneIcon sx={{ fontSize: 13 }} />}
                        label={`${adjustments.length} adjustment${adjustments.length === 1 ? '' : 's'}`}
                        sx={{ height: 20, fontSize: 11 }} />
                    </Tooltip>
                  )}
                  {f.skip_file && (
                    <Tooltip title={f.skip_reason || 'Not imported'}>
                      <Chip size="small" variant="outlined" icon={<VisibilityOffIcon sx={{ fontSize: 13 }} />}
                        label="not imported" sx={{ height: 20, fontSize: 11 }} />
                    </Tooltip>
                  )}
                </>
              )}
              headerAction={(
                <Tooltip title={f.skip_file ? 'Include this file in the import' : 'Exclude this file from the import'}>
                  <Switch size="small" checked={!f.skip_file}
                    onChange={(e) => setFile(f.filename, {
                      skip_file: !e.target.checked,
                      skip_reason: e.target.checked ? '' : (f.skip_reason || 'excluded by user'),
                    })} />
                </Tooltip>
              )}
              detailsTop={adjustments.length > 0 ? (
                <Box sx={{ mb: 1 }}>
                  {adjustments.map((d, i) => <DecisionRow key={i} decision={d} />)}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ) : null}
            />
          );
        })}
      </Stack>

      {missingTimestamp.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Every imported file needs a time column. Open Configure on{' '}
          {missingTimestamp.length === 1 ? <strong>{missingTimestamp[0]}</strong> : `${missingTimestamp.length} files`}
          {' '}and tap <strong>time</strong> on the timestamp column, or switch the file off.
        </Alert>
      )}

      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button onClick={onBack} disabled={importing} sx={{ textTransform: 'none' }}>Back</Button>
        <Button variant="contained" disabled={importing || importableCount === 0 || missingTimestamp.length > 0} onClick={runImport}
          startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <BoltIcon />}
          sx={{ textTransform: 'none', px: 3 }}>
          {importing ? 'Starting import…' : `Import ${importableCount} file${importableCount === 1 ? '' : 's'}`}
        </Button>
      </Stack>
    </Box>
  );
}
